import {
  ComponentPropsWithRef,
  ComponentType,
  forwardRef,
  InputHTMLAttributes,
  ReactNode,
  Ref,
} from 'react';
import { cva, VariantProps } from 'class-variance-authority';
import { AnimatePresence, motion } from 'framer-motion';
import IconButton from '@/components/ui/buttons/icon-button';
import TextInput from '@/components/ui/inputs/text-input';
import Label from '@/components/ui/label';
import BodyText from '@/components/ui/typography/body-text';
import { cn } from '@/lib/utils';
import fadeInOutMotion from '@/styles/motions';

type TextFieldProps = ComponentPropsWithRef<'div'> &
  VariantProps<typeof textFieldVariants> & {
    label?: string;
    labelPosition?: 'top' | 'left';
    size?: 'sm' | 'md' | 'lg';
    inputRef?: Ref<HTMLInputElement>;
    placeholder?: string;
    required?: boolean;
    inputName?: InputHTMLAttributes<HTMLInputElement>['name'];
    inputValue?: InputHTMLAttributes<HTMLInputElement>['value'];
    inputType?: InputHTMLAttributes<HTMLInputElement>['type'];
    onInputChange?: (newValue: string) => void;
    isError?: boolean;
    error?: string;
    disabled?: boolean;
    clearable?: boolean;
    startElement?: ReactNode;
    endElement?: ReactNode;
    infoPopover?: ComponentType | null;
  };

const TextField = forwardRef<HTMLDivElement, TextFieldProps>(
  (
    {
      label,
      size = 'sm',
      inputRef = null,
      placeholder = '',
      required = false,
      inputName,
      inputValue = '',
      inputType = 'text',
      // eslint-disable-next-line no-console
      onInputChange = (newValue) => console.log(newValue),
      disabled = false,
      clearable = false,
      isError = false,
      error,
      startElement,
      endElement,
      infoPopover: InfoPopover,
      className,
      ...props
    },
    ref
  ) => {
    const onClearClick = () => {
      onInputChange('');
    };
    return (
      <div className="w-full">
        {label ? (
          <div className="flex items-center gap-x-1 mb-1">
            <Label>{`${label}`}</Label>
            {required ? (
              <BodyText variant="caption" className="text-error">
                *
              </BodyText>
            ) : null}
            {InfoPopover ? <InfoPopover /> : null}
          </div>
        ) : null}
        <div
          ref={ref}
          aria-disabled={disabled}
          data-error={isError.toString()}
          className={cn(textFieldVariants({ size, className }))}
          {...props}>
          {startElement ?? null}
          <TextInput
            type={inputType}
            ref={inputRef}
            placeholder={placeholder}
            name={inputName}
            value={inputValue}
            onChange={(e) => onInputChange(e.target.value)}
            className="grow"
            disabled={disabled}
          />
          {clearable ? (
            <IconButton icon="close" size="3xs" color="achromatic" onClick={onClearClick} />
          ) : null}
          {endElement ?? null}
        </div>
        <AnimatePresence>
          <motion.div
            initial="fade-out"
            variants={fadeInOutMotion()}
            animate={isError ? 'fade-in' : 'fade-out'}
            className="mt-1">
            <BodyText variant="caption" className="text-error">
              {error}
            </BodyText>
          </motion.div>
        </AnimatePresence>
      </div>
    );
  }
);
TextField.displayName = 'TextField';

const textFieldVariants = cva(
  [
    'flex',
    'items-center',
    'gap-x-2',
    'bg-white',
    'border-0',
    'px-2',
    'rounded-5',
    'shadow-input',
    'focus-within:shadow-input-focus',
    'data-[error=true]:shadow-input-error',
    'data-[error=true]:text-error',
    'transition-all',
    'aria-disabled:cursor-not-allowed',
    'aria-disabled:text-achromatic-disabled-dark',
    'aria-disabled:bg-achromatic-disabled-light',
  ],
  {
    variants: {
      size: {
        sm: ['h-[1.875rem]'],
        md: ['h-9'],
        lg: ['h-11'],
      },
    },
  }
);

export default TextField;
